import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Send, 
  Users, 
  Mail, 
  Loader2, 
  CheckCircle2, 
  AlertCircle,
  History,
  TestTube,
  Megaphone,
  GraduationCap,
  UserCircle,
  Target,
  Paperclip,
  Link2,
  X,
  FileText,
  Upload
} from 'lucide-react';
import api from '../services/api';

const SEGMENTS = [
  { value: 'all', label: 'All Users', icon: Users, description: 'Enrolled users + leads' },
  { value: 'athletes', label: 'Athletes Only', icon: GraduationCap, description: 'Enrolled student athletes' },
  { value: 'parents', label: 'Parents Only', icon: UserCircle, description: 'Scholar Parent Academy members' },
  { value: 'leads', label: 'Leads Only', icon: Target, description: 'Waitlist (not yet enrolled)' },
];

const AdminCommunications = () => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [segment, setSegment] = useState('all');
  const [segmentCount, setSegmentCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  
  // Attachment states
  const [directAttachments, setDirectAttachments] = useState([]); // Files to attach directly
  const [linkAttachments, setLinkAttachments] = useState([]); // URLs to include as links
  const [uploadingFile, setUploadingFile] = useState(false);
  const directFileRef = useRef(null);
  const linkFileRef = useRef(null);

  // Fetch segment count when segment changes
  useEffect(() => {
    if (authenticated) {
      fetchSegmentCount(segment);
    }
  }, [segment, authenticated]);

  // Fetch broadcast history on load
  useEffect(() => {
    if (authenticated) {
      fetchBroadcastHistory();
    }
  }, [authenticated]);

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    
    try {
      const response = await api.post('/academy/admin/verify', { password });
      if (response.data.success) {
        setAuthenticated(true);
        toast.success('Admin access granted');
      }
    } catch (error) {
      toast.error('Invalid admin password');
    } finally {
      setAuthLoading(false);
    }
  };

  const fetchSegmentCount = async (seg) => {
    try {
      const response = await api.post('/academy/admin/segment-count', { segment: seg });
      if (response.data.success) {
        setSegmentCount(response.data.count);
      }
    } catch (error) {
      console.error('Error fetching segment count:', error);
    }
  };

  const fetchBroadcastHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await api.get('/academy/admin/broadcasts');
      if (response.data.success) {
        setBroadcasts(response.data.broadcasts || []);
      }
    } catch (error) {
      console.error('Error fetching broadcast history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Handle direct file attachment (will be sent as email attachment)
  const handleDirectFileSelect = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit for direct attachments
        toast.error(`${file.name} is too large. Max 10MB for direct attachments.`);
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        setDirectAttachments(prev => [...prev, {
          filename: file.name,
          content: base64,
          content_type: file.type,
          size: file.size
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = ''; // Reset input
  };

  // Handle link-based file upload (file hosted on server, link in email)
  const handleLinkFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 50 * 1024 * 1024) { // 50MB limit for hosted files
      toast.error('File too large. Max 50MB.');
      return;
    }
    
    setUploadingFile(true);
    
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const response = await api.post('/academy/admin/upload-broadcast-file', {
            file_data: reader.result,
            file_name: file.name,
            file_type: file.type
          });
          
          if (response.data.success) {
            setLinkAttachments(prev => [...prev, {
              url: response.data.file_url,
              filename: file.name,
              size: file.size
            }]);
            toast.success(`${file.name} uploaded!`);
          }
        } catch (error) {
          toast.error('Failed to upload file');
        } finally {
          setUploadingFile(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to read file');
      setUploadingFile(false);
    }
    
    e.target.value = ''; // Reset input
  };

  const removeDirectAttachment = (index) => {
    setDirectAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const removeLinkAttachment = (index) => {
    setLinkAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleTestSend = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error('Please enter both subject and message body');
      return;
    }

    setTestLoading(true);
    
    try {
      const response = await api.post('/academy/admin/broadcast', {
        subject,
        body_html: formatBodyToHtml(body),
        segment,
        test_mode: true,
        attachments: directAttachments.length > 0 ? directAttachments : null,
        attachment_links: linkAttachments.length > 0 ? linkAttachments.map(a => a.url) : null
      });
      
      if (response.data.success) {
        toast.success('Test email sent to admin inbox!');
      } else {
        toast.error(response.data.message || 'Failed to send test email');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestLoading(false);
    }
  };

  const handleBroadcast = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error('Please enter both subject and message body');
      return;
    }

    if (segmentCount === 0) {
      toast.error('No recipients in selected segment');
      return;
    }

    // Confirm before sending
    let confirmMsg = `Are you sure you want to send this email to ${segmentCount} recipient(s) in the "${SEGMENTS.find(s => s.value === segment)?.label}" segment?`;
    if (directAttachments.length > 0) {
      confirmMsg += `\n\nDirect attachments: ${directAttachments.length} file(s)`;
    }
    if (linkAttachments.length > 0) {
      confirmMsg += `\n\nLinked files: ${linkAttachments.length} file(s)`;
    }
    
    const confirmed = window.confirm(confirmMsg);

    if (!confirmed) return;

    setLoading(true);
    
    try {
      const response = await api.post('/academy/admin/broadcast', {
        subject,
        body_html: formatBodyToHtml(body),
        segment,
        test_mode: false,
        attachments: directAttachments.length > 0 ? directAttachments : null,
        attachment_links: linkAttachments.length > 0 ? linkAttachments.map(a => a.url) : null
      });
      
      if (response.data.success) {
        toast.success(`Broadcast sent to ${response.data.sent_count} recipients!`);
        // Clear form
        setSubject('');
        setBody('');
        setDirectAttachments([]);
        setLinkAttachments([]);
        // Refresh history
        fetchBroadcastHistory();
      } else {
        toast.error(response.data.message || 'Failed to send broadcast');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send broadcast');
    } finally {
      setLoading(false);
    }
  };

  const formatBodyToHtml = (text) => {
    // Convert plain text to styled HTML
    const paragraphs = text.split('\n\n').filter(p => p.trim());
    const htmlParagraphs = paragraphs.map(p => {
      // Handle single line breaks within paragraphs
      const lines = p.split('\n').join('<br>');
      return `<p style="font-size: 16px; color: #ccc; margin-bottom: 20px; line-height: 1.8;">${lines}</p>`;
    }).join('');
    
    return htmlParagraphs;
  };

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Auth screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-gray-900 border-gray-800">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Megaphone className="w-8 h-8 text-orange-500" />
            </div>
            <CardTitle className="text-white text-2xl">Admin Communications</CardTitle>
            <CardDescription className="text-gray-400">
              Enter admin password to access the broadcast center
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAuth} className="space-y-4">
              <div>
                <Label htmlFor="password" className="text-gray-300">Admin Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white mt-1"
                  placeholder="Enter password"
                  data-testid="admin-password-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-orange-500 hover:bg-orange-600 text-black font-bold"
                disabled={authLoading}
                data-testid="admin-login-btn"
              >
                {authLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Access Dashboard'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500/10 to-amber-500/5 border-b border-gray-800">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-orange-500/20 rounded-xl flex items-center justify-center">
              <Megaphone className="w-7 h-7 text-orange-500" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-white">Communications Center</h1>
              <p className="text-gray-400">Broadcast messages to your roster</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Mail className="w-5 h-5 text-orange-500" />
                  Compose Broadcast
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Create and send emails to your roster segments
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Segment Selector */}
                <div className="space-y-2">
                  <Label className="text-gray-300">Target Segment</Label>
                  <Select value={segment} onValueChange={setSegment}>
                    <SelectTrigger 
                      className="bg-gray-800 border-gray-700 text-white"
                      data-testid="segment-select"
                    >
                      <SelectValue placeholder="Select segment" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      {SEGMENTS.map((seg) => (
                        <SelectItem 
                          key={seg.value} 
                          value={seg.value}
                          className="text-white hover:bg-gray-700 focus:bg-gray-700"
                        >
                          <div className="flex items-center gap-2">
                            <seg.icon className="w-4 h-4 text-orange-500" />
                            <span>{seg.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-gray-500">
                    {SEGMENTS.find(s => s.value === segment)?.description}
                  </p>
                </div>

                {/* Recipient Count */}
                <div className="bg-gray-800/50 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-gray-400">
                    <Users className="w-4 h-4" />
                    <span>Recipients in segment:</span>
                  </div>
                  <Badge variant="secondary" className="bg-orange-500/20 text-orange-500 font-bold text-lg px-3">
                    {segmentCount}
                  </Badge>
                </div>

                {/* Subject */}
                <div className="space-y-2">
                  <Label htmlFor="subject" className="text-gray-300">Subject Line</Label>
                  <Input
                    id="subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Enter email subject..."
                    className="bg-gray-800 border-gray-700 text-white"
                    data-testid="email-subject-input"
                  />
                </div>

                {/* Body */}
                <div className="space-y-2">
                  <Label htmlFor="body" className="text-gray-300">Message Body</Label>
                  <Textarea
                    id="body"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Write your message here...

Use blank lines to separate paragraphs.
The email will be wrapped in PRSVR Academy branding automatically."
                    className="bg-gray-800 border-gray-700 text-white min-h-[200px] resize-y"
                    data-testid="email-body-input"
                  />
                  <p className="text-xs text-gray-500">
                    Tip: Use blank lines to create paragraph breaks. Your message will be styled with PRSVR Academy branding.
                  </p>
                </div>

                {/* Attachments Section */}
                <div className="space-y-4 pt-4 border-t border-gray-800">
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    Attachments
                  </Label>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    {/* Direct Attachments */}
                    <div className="bg-gray-800/50 rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Paperclip className="w-4 h-4 text-orange-500" />
                          <span className="text-sm font-medium text-white">Direct Attach</span>
                        </div>
                        <Badge variant="outline" className="text-xs border-gray-600 text-gray-400">Max 10MB</Badge>
                      </div>
                      <p className="text-xs text-gray-500">File attached directly to email</p>
                      
                      <input
                        type="file"
                        ref={directFileRef}
                        onChange={handleDirectFileSelect}
                        className="hidden"
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                        multiple
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => directFileRef.current?.click()}
                        className="w-full border-gray-600 text-gray-300 hover:bg-gray-700"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Add File
                      </Button>
                      
                      {/* Direct attachment list */}
                      {directAttachments.length > 0 && (
                        <div className="space-y-2 mt-2">
                          {directAttachments.map((file, index) => (
                            <div key={index} className="flex items-center justify-between bg-gray-900 rounded p-2 text-xs">
                              <div className="flex items-center gap-2 truncate">
                                <FileText className="w-3 h-3 text-orange-500 shrink-0" />
                                <span className="text-gray-300 truncate">{file.filename}</span>
                                <span className="text-gray-500">({formatFileSize(file.size)})</span>
                              </div>
                              <button
                                onClick={() => removeDirectAttachment(index)}
                                className="text-gray-500 hover:text-red-500 shrink-0"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Link-based Attachments */}
                    <div className="bg-gray-800/50 rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Link2 className="w-4 h-4 text-orange-500" />
                          <span className="text-sm font-medium text-white">Link Attach</span>
                        </div>
                        <Badge variant="outline" className="text-xs border-gray-600 text-gray-400">Max 50MB</Badge>
                      </div>
                      <p className="text-xs text-gray-500">Download link in email body</p>
                      
                      <input
                        type="file"
                        ref={linkFileRef}
                        onChange={handleLinkFileSelect}
                        className="hidden"
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.mp4,.zip"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => linkFileRef.current?.click()}
                        disabled={uploadingFile}
                        className="w-full border-gray-600 text-gray-300 hover:bg-gray-700"
                      >
                        {uploadingFile ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        {uploadingFile ? 'Uploading...' : 'Upload & Link'}
                      </Button>
                      
                      {/* Link attachment list */}
                      {linkAttachments.length > 0 && (
                        <div className="space-y-2 mt-2">
                          {linkAttachments.map((file, index) => (
                            <div key={index} className="flex items-center justify-between bg-gray-900 rounded p-2 text-xs">
                              <div className="flex items-center gap-2 truncate">
                                <Link2 className="w-3 h-3 text-orange-500 shrink-0" />
                                <span className="text-gray-300 truncate">{file.filename}</span>
                                <span className="text-gray-500">({formatFileSize(file.size)})</span>
                              </div>
                              <button
                                onClick={() => removeLinkAttachment(index)}
                                className="text-gray-500 hover:text-red-500 shrink-0"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-800">
                  <Button
                    variant="outline"
                    onClick={handleTestSend}
                    disabled={testLoading || !subject.trim() || !body.trim()}
                    className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800"
                    data-testid="test-send-btn"
                  >
                    {testLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <TestTube className="w-4 h-4 mr-2" />
                    )}
                    Send Test to Admin
                  </Button>
                  <Button
                    onClick={handleBroadcast}
                    disabled={loading || !subject.trim() || !body.trim() || segmentCount === 0}
                    className="flex-1 bg-orange-500 hover:bg-orange-600 text-black font-bold"
                    data-testid="broadcast-send-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Broadcast to {segmentCount} Recipients
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Broadcast History */}
          <div className="space-y-6">
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2 text-lg">
                  <History className="w-5 h-5 text-orange-500" />
                  Recent Broadcasts
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingHistory ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                  </div>
                ) : broadcasts.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Mail className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>No broadcasts sent yet</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {broadcasts.map((broadcast) => (
                      <div 
                        key={broadcast.id}
                        className="bg-gray-800/50 rounded-lg p-3 space-y-2"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-white font-medium text-sm line-clamp-2">
                            {broadcast.subject}
                          </p>
                          {broadcast.test_mode && (
                            <Badge variant="outline" className="text-xs border-yellow-500/50 text-yellow-500 shrink-0">
                              TEST
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {broadcast.segment}
                          </span>
                          <span className="flex items-center gap-1">
                            {broadcast.sent_count > 0 ? (
                              <CheckCircle2 className="w-3 h-3 text-green-500" />
                            ) : (
                              <AlertCircle className="w-3 h-3 text-red-500" />
                            )}
                            {broadcast.sent_count}/{broadcast.recipient_count}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600">
                          {formatDate(broadcast.created_at)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white text-lg">Segment Overview</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {SEGMENTS.map((seg) => (
                  <SegmentStatRow key={seg.value} segment={seg} />
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// Segment stat row component
const SegmentStatRow = ({ segment }) => {
  const [count, setCount] = useState(null);
  
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const response = await api.post('/academy/admin/segment-count', { segment: segment.value });
        if (response.data.success) {
          setCount(response.data.count);
        }
      } catch (error) {
        setCount(0);
      }
    };
    fetchCount();
  }, [segment.value]);

  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-800 last:border-0">
      <div className="flex items-center gap-2 text-gray-400">
        <segment.icon className="w-4 h-4" />
        <span className="text-sm">{segment.label}</span>
      </div>
      <span className="text-white font-bold">
        {count === null ? '...' : count}
      </span>
    </div>
  );
};

export default AdminCommunications;
